import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import Axios from "axios";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import ConfirmModal from "./confirmModal";
import getCookie from "../../utils/getCookies";

export default function ManualModal({ open, onClose, _course = null, fromWeekly = false }) {
  const [semester, setSemester] = useState(null);
  const [course, setCourse] = useState(null);
  const [event, setEvent] = useState(null);
  const [teacherId, setTeacherId] = useState("");
  const [teacherEmail, setTeacherEmail] = useState("");
  const [semestersData, setSemestersData] = useState([]);
  const [coursesData, setCoursesData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [allowSunday, setAllowSunday] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);

  const [step, setStep] = useState(0);

  const steps = [
    "Select Semester",
    "Select Course",
    "Select Event",
    "Confirm & Create",
  ];

  const handleNext = () => {
    if (fromWeekly) {
      // Only allow navigation between step 2 and 3
      if (step === 2) setStep(3);
    } else {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (fromWeekly) {
      // Only allow navigation between step 3 and 2
      if (step === 3) setStep(2);
    } else {
      setStep((prevStep) => {
        if (prevStep === 3) {
          setEvent(null); // step 2
          setCourseSearch(""); // clear search
          setFilteredCourses([]);
        } else if (prevStep === 2) {
          setEvent(null); // step 2
          setCourse(null); // step 1
          setTeacherId("");
          setTeacherEmail("");
          setCourseSearch(""); // clear search
          setFilteredCourses([]);
        } else if (prevStep === 1) {
          setSemester(null); // step 0
          setTeacherId("");
          setTeacherEmail("");
          setCourseSearch(""); // clear search
          setCourse(null);
          setFilteredCourses([]);
        }
        return prevStep - 1;
      });
    }
  };

  const handleClose = () => {
    setStep(0);
    setCourseSearch("");
    setFilteredCourses([]);
    onClose();
    setSemester(null);
    setCourse(null);
    setEvent(null);
    setConfirmSuccess(false);
    setConfirmError(false);
  };

  const parseSemester = (semester) => {
    const { year, season } = semester;
    const seasonMap = {
      0: "All",
      1: "Spring",
      2: "Summer",
      3: "Fall",
    };
    return `${year} ${seasonMap[season]}`;
  };

  useEffect(() => {
    if (_course) {
        setCourse(_course);
        setStep(2); // jump to event selection
    }
  }, []);

  useEffect(() => {
    const fetchSemesters = async () => {
      const response = await Axios.get(
        `${process.env.REACT_APP_URL}/api/v1/semesters`,
        {
          headers: {
            Authorization: `Bearer ${getCookie('_auth')}`,
          },
        }
      );
      setSemestersData(response.data);
    };
    fetchSemesters();
  }, []);

  useEffect(() => {
    if (!semester) return;
    const fetchCourses = async () => {
      const response = await Axios.get(
        `${process.env.REACT_APP_URL}/api/v1/courses/semester/${semester?.id}`,
        {
          headers: {
            Authorization: `Bearer ${getCookie('_auth')}`,
          },
        }
      );
      setCoursesData(response.data);
    };
    fetchCourses();
  }, [semester]);

  useEffect(() => {
    if (!course) return;
    if (course.teacher_courses.length === 1) {
      setTeacherId(course.teacher_courses[0].teacher.id);
      setTeacherEmail(course.teacher_courses[0].teacher.email);
    }
    const fetchEvents = async () => {
      const response = await Axios.get(
        `${process.env.REACT_APP_URL}/api/v1/courses/${course.uuid}/events/manual`,
        {
          headers: {
            Authorization: `Bearer ${getCookie('_auth')}`,
          },
        }
      );
      const excludeMakeupEvents = response.data.filter((event) => {
        if (event.is_make_up_event === false && event.is_canceled === false) {
          return event;
        }
      });
      setEventsData(excludeMakeupEvents);
    };

    const fetchAllStudents = async () => {
      const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/makeup/student_courses/${course.uuid}`, {
        headers: {
          Authorization: `Bearer ${getCookie('_auth')}`,
        },
      })
      setAllStudents(response.data);
    }
    fetchAllStudents();
    fetchEvents();
  }, [course]);

  const shouldDisableDate = (date) => {
    if (!eventsData || eventsData.length === 0) return true;

    const dateString = date.format("YYYY-MM-DD");
    const todayString = dayjs().format("YYYY-MM-DD");

    // Only allow dates that are after today and match an event
    return !eventsData.some(
      (event) =>
        DateTime.fromISO(event.start).toFormat("yyyy-MM-dd") === dateString &&
        dateString >= todayString // strictly today or after today
    );
  };

  const filterStudentsByEventDate = (selectedDate) => {
    if (!allStudents || allStudents.length === 0 || !selectedDate) {
      setFilteredStudents([]);
      return;
    }

    const eventDate = DateTime.fromISO(selectedDate);
    
    const validStudents = allStudents.filter((studentCourse) => {
      const startDate = DateTime.fromJSDate(new Date(studentCourse.start_date));
      const endDate = DateTime.fromJSDate(new Date(studentCourse.end_date));
      
      // Student is valid if start_date is before or equal to event date AND end_date is after or equal to event date
      return startDate <= eventDate && endDate >= eventDate;
    });

    setFilteredStudents(validStudents);
  };

  const handleDateChange = (newValue) => {
    if (!newValue) {
      setEvent(null);
      setFilteredStudents([]);
      return;
    }

    const selectedDate = newValue.format("YYYY-MM-DD");
    const selectedEvent = eventsData.find(
      (event) =>
        DateTime.fromISO(event.start).toFormat("yyyy-MM-dd") === selectedDate
    );

    setEvent(selectedEvent);
    
    // Filter students based on the selected event date
    if (selectedEvent) {
      filterStudentsByEventDate(selectedEvent.start);
    } else {
      setFilteredStudents([]);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Semester
            </Typography>
            <Select
              fullWidth
              value={semester?.id || ""}
              onChange={(e) => {
                const selectedSemester = semestersData.find(
                  (semester) => semester.id === e.target.value
                );
                setSemester(selectedSemester);
              }}
              margin="normal"
            >
              {semestersData.map((semester) => (
                <MenuItem key={semester.id} value={semester.id}>
                  {parseSemester(semester)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Course
            </Typography>
            <TextField
              fullWidth
              label="Search by course code or name"
              value={courseSearch || ""}
              onChange={(e) => {
                const searchTerm = e.target.value;
                setCourseSearch(searchTerm);

                // Filter courses based on search term
                const filtered = coursesData.filter(
                  (course) =>
                    course.code
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    course.course_title
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase())
                );
                setFilteredCourses(filtered);

                // Clear selected course if search doesn't match
                if (course && !filtered.find((c) => c.id === course.id)) {
                  setCourse(null);
                }
              }}
              margin="normal"
            />
            {courseSearch && filteredCourses.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflow: "auto",
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                {filteredCourses.map((course) => (
                  <Box
                    key={course.id}
                    sx={{
                      p: 1,
                      cursor: "pointer",
                      backgroundColor:
                        course?.id === course.id ? "primary.50" : "white",
                      "&:hover": {
                        backgroundColor:
                          course?.id === course.id ? "primary.100" : "grey.100",
                      },
                    }}
                    onClick={() => {
                      setCourse(course);
                      setCourseSearch(
                        `${course.code} - ${course.course_title}`
                      );
                      setFilteredCourses([]);
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{course.code}</strong> {course.course_title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {courseSearch && filteredCourses.length === 0 && !course && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No courses found matching "{courseSearch}"
              </Typography>
            )}
            {course && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" color="primary">
                  Selected Course:
                </Typography>
                <Typography variant="body2">
                  <strong>{course.code}</strong> - {course.course_title}
                </Typography>
              </Box>
            )}
            {course && course.teacher_courses.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary">
                  Select Teacher
                </Typography>
                <Select
                  fullWidth
                  value={teacherId}
                  onChange={(e) => {
                    setTeacherId(e.target.value);
                    const selectedTeacher = course.teacher_courses.find(
                      (tc) => tc.teacher.id === parseInt(e.target.value)
                    );
                    if (selectedTeacher)
                      setTeacherEmail(selectedTeacher.teacher.email);
                  }}
                >
                  {course.teacher_courses.map((teacherCourse) => (
                    <MenuItem
                      key={teacherCourse.teacher.id}
                      value={teacherCourse.teacher.id}
                    >
                      {teacherCourse.teacher.first_name}{" "}
                      {teacherCourse.teacher.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Event
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Event Date"
                value={event ? dayjs(event.start) : null}
                onChange={handleDateChange}
                shouldDisableDate={shouldDisableDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    readOnly: true,
                    helperText:
                      eventsData.length > 0
                        ? `${eventsData.length} event(s) available`
                        : "No events available for this course",
                  },
                  day: (ownerState) => {
                    const isAllowed = !shouldDisableDate(ownerState.day);
                    return {
                      sx: isAllowed
                        ? {
                            backgroundColor: "#e6f9ec", // very light green
                            color: "black",
                            fontWeight: "bold",
                            "&:hover": {
                              backgroundColor: "#b2f2c9", // slightly darker green
                            },
                          }
                        : {},
                    };
                  },
                }}
              />
            </LocalizationProvider>

            {event && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" color="primary">
                  Selected Event:
                </Typography>
                <Typography variant="body2">
                  Date: {DateTime.fromISO(event.start).toFormat("yyyy-MM-dd")}
                  {event.title && ` - ${event.title}`}
                </Typography>
              </Box>
            )}

            {event && filteredStudents.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "blue.50",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "blue.200",
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Affected Students ({filteredStudents.length}):
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {filteredStudents.map((studentCourse, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        py: 0.5,
                        borderBottom: index < filteredStudents.length - 1 ? "1px solid #e0e0e0" : "none",
                      }}
                    >
                      {studentCourse.student.email_code && `(${studentCourse.student.email_code}) `}
                      {studentCourse.student.consultation_number && `${studentCourse.student.consultation_number} - `}
                      {studentCourse.student.first_name} {studentCourse.student.last_name}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {event && filteredStudents.length === 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "orange.50",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "orange.200",
                }}
              >
                <Typography variant="subtitle2" color="warning.main">
                  No students are affected by this event date.
                </Typography>
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h4" gutterBottom>
              Confirm Details
            </Typography>
            <Typography variant="h5" color="error" gutterBottom>
              You are about to initiate a manual makeup loop for the following
              course.
              <br />
              Creating the loop will send out a scheduling email to the teacher.
              <br />
              Minimum Window Start Date could be pushed back if schedule
              conflicts or other issues arise.
              <br />
            </Typography>
            {semester && (
                <Typography
                variant="h5"
                sx={{ mt: 2, fontWeight: "bold" }}
                gutterBottom
                >
                Semester: {parseSemester(semester)}
                </Typography>
            )}
            <Typography variant="h5" sx={{ fontWeight: "bold" }} gutterBottom>
              Course: {course ? `${course.code} - ${course.course_title}` : ""}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }} gutterBottom>
              Minimum Window Start Date:{" "}
              {event
                ? `${DateTime.fromISO(event.start)
                    .plus({ days: 3 })
                    .toFormat("yyyy-MM-dd")}`
                : ""}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }} gutterBottom>
              Teacher Email: {teacherEmail}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap:0 }}>
            <Checkbox
              checked={allowSunday}
              onChange={(e) => setAllowSunday(e.target.checked)}
            />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Allow Sunday
            </Typography>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const openConfirmModal = () => {
    setConfirmModalData({
      semester_id: semester?.id,
      course_id: course?.id,
      event_id: event?.id,
      teacher_email: teacherEmail,
      teacher_id: teacherId,
      allow_sunday: allowSunday,
    });
    setConfirmModalOpen(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {confirmSuccess ? (
        <>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
              flexGrow: 1,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
            >
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#4caf50" />
                <path
                  d="M7 13.5L10.5 17L17 10.5"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <Typography
                variant="h5"
                color="success"
                align="center"
                sx={{ mt: 3 }}
              >
                Makeup loop created successfully!
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </>
      ) : confirmError ? (
        <>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 300,
              flexGrow: 1,
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
            >
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="12" fill="#f44336" />
                <path
                  d="M8 8L16 16"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <path
                  d="M16 8L8 16"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
              <Typography
                variant="h5"
                color="error"
                align="center"
                sx={{ mt: 3 }}
              >
                Failed to create makeup loop
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Close</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            <Stepper activeStep={step} sx={{ mt: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <LinearProgress
              variant="determinate"
              value={(step / (steps.length - 1)) * 100}
              sx={{ mt: 2, height: 8, borderRadius: 4 }}
            />
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, minHeight: 200 }}>{renderStepContent()}</Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            {/* Only show Back button if allowed */}
            {(!fromWeekly && step > 0) || (fromWeekly && step === 3) ? (
              <Button onClick={handleBack}>Back</Button>
            ) : null}
            {/* Only show Next button if allowed */}
            {(!fromWeekly && step < steps.length - 1) || (fromWeekly && step === 2) ? (
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={
                  (step === 0 && !semester) ||
                  (step === 1 && !course) ||
                  (step === 2 && !event)
                }
              >
                Next
              </Button>
            ) : null}
            {/* Only show Create button on step 3 */}
            {step === 3 && (
              <Button
                onClick={openConfirmModal}
                variant="contained"
                color="success"
              >
                Create
              </Button>
            )}
          </DialogActions>
        </>
      )}
      <ConfirmModal
        data={confirmModalData}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onSuccess={() => {
          setConfirmModalOpen(false);
          setConfirmSuccess(true);
        }}
        onError={() => {
          setConfirmModalOpen(false);
          setConfirmError(true);
        }}
      />
    </Dialog>
  );
}
